/**
 * Activity Type Enumeration
 * Defines all possible activity types that can be logged in the system
 */
export enum ActivityType {
  // Product activities
  PRODUCT_CREATED = 'product_created',
  PRODUCT_UPDATED = 'product_updated',
  PRODUCT_DELETED = 'product_deleted',
  PRODUCT_IMAGE_UPLOADED = 'product_image_uploaded',

  // Order activities
  ORDER_CREATED = 'order_created',
  ORDER_UPDATED = 'order_updated',
  ORDER_STATUS_CHANGED = 'order_status_changed',
  ORDER_CANCELLED = 'order_cancelled',
  ORDER_DELIVERED = 'order_delivered',

  // Customer activities
  CUSTOMER_REGISTERED = 'customer_registered',
  CUSTOMER_UPDATED = 'customer_updated',
  CUSTOMER_DELETED = 'customer_deleted',

  // Organization activities
  ORGANIZATION_CREATED = 'organization_created',
  ORGANIZATION_UPDATED = 'organization_updated',
  ORGANIZATION_DELETED = 'organization_deleted',

  // Employee activities
  EMPLOYEE_ADDED = 'employee_added',
  EMPLOYEE_UPDATED = 'employee_updated',
  EMPLOYEE_REMOVED = 'employee_removed',

  // Category activities
  CATEGORY_CREATED = 'category_created',
  CATEGORY_UPDATED = 'category_updated',
  CATEGORY_DELETED = 'category_deleted',

  // Cart activities
  CART_ITEM_ADDED = 'cart_item_added',
  CART_ITEM_REMOVED = 'cart_item_removed',
  CART_UPDATED = 'cart_updated',

  // Recognition activities
  RECOGNITION_TASK_CREATED = 'recognition_task_created',
  RECOGNITION_TASK_COMPLETED = 'recognition_task_completed',
  REWARD_CLAIMED = 'reward_claimed',
  PEER_RECOGNITION_SENT = 'peer_recognition_sent',

  // User activities
  USER_LOGIN = 'user_login',
  USER_LOGOUT = 'user_logout',
  PASSWORD_CHANGED = 'password_changed',

  // Settings activities
  SETTINGS_UPDATED = 'settings_updated',
  BANNER_CREATED = 'banner_created',
  BANNER_UPDATED = 'banner_updated',
  BANNER_DELETED = 'banner_deleted',
}

/**
 * Activity Log Interface
 * Represents a logged user action in the system
 */
export interface ActivityLog {
  _id: string;
  userId: string;
  userName: string;
  userRole: 'superadmin' | 'admin' | 'user';
  activityType: ActivityType;
  action: 'created' | 'updated' | 'deleted' | 'uploaded' | 'changed' | 'completed' | 'claimed' | 'sent' | 'login' | 'logout';
  targetType: string;
  targetId: string | null;
  targetName: string;
  description: string;
  metadata?: {
    changes?: any;
    quantity?: number;
    amount?: number;
    status?: string;
    [key: string]: any;
  };
  organizationId: string | null;
  ipAddress?: string;
  userAgent?: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  createdAt: string;
}

/**
 * Create Activity Payload Interface
 * Used when creating a new activity log entry
 */
export interface CreateActivityPayload {
  activityType: ActivityType;
  action: string;
  targetType: string;
  targetId?: string | null;
  targetName: string;
  description: string;
  metadata?: {
    [key: string]: any;
  };
  endpoint: string;
  method: string;
}

/**
 * Activity Query Parameters Interface
 * Used for filtering and paginating activity logs
 */
export interface ActivityQueryParams {
  type?: ActivityType;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

/**
 * Activity Response Interface
 * Response structure from activity API endpoints
 */
export interface ActivityResponse {
  success: boolean;
  data: {
    activities: ActivityLog[];
    total: number;
    page: number;
    pages: number;
  };
}

/**
 * Activity Filter State Interface
 * Used in Zustand store for activity filtering
 */
export interface ActivityFilter {
  type: ActivityType | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  userId: string | null;
}
